<template>
  <PageChrome>
    <template #title>Customer List</template>

    <template #actions>
      <span v-if="lastSyncedDisplay" class="muted" style="font-size:13px;">Last synced: {{ lastSyncedDisplay }}</span>
    </template>

    <div v-if="error" class="error">{{ error }}</div>

    <div class="list-panel">
      <div class="list-panel-header">
        <div class="list-panel-title">Customer accounts <span class="muted">({{ entries.length }})</span></div>
      </div>

      <div v-if="loading" class="list-panel-empty">Loading…</div>
      <div v-else-if="entries.length === 0" class="list-panel-empty">
        No customer list entries yet. Connect Salesforce and enable the customer list sync — the list syncs daily from Closed Won opportunities.
      </div>

      <div v-else class="list">
        <div v-for="entry in entries" :key="entry.id" class="row">
          <HeaderWithIcon
            :domain="entry.domain || ''"
            :label="entry.name || entry.domain || entry.salesforce_account_id"
            :secondaryText="entry.domain || ''"
          />

          <div class="statCol">
            <div class="statLabel">Last Won</div>
            <div class="statValue">{{ entry.last_closed_won_date ? fmtDate(entry.last_closed_won_date) : '—' }}</div>
          </div>

          <div class="statCol">
            <div class="statLabel">Closed Won</div>
            <div class="statValue">
              {{ entry.opportunity_count }} opp{{ entry.opportunity_count !== 1 ? 's' : '' }}
              <span v-if="entry.total_amount != null" class="muted"> · {{ fmtAmount(entry.total_amount) }}</span>
            </div>
          </div>

          <div class="notesPreview">
            <span v-if="entry.notes" class="notesText muted">{{ entry.notes }}</span>
            <span v-else class="muted noNotes">No notes</span>
          </div>

          <button class="editBtn" :title="entry.notes ? 'Edit notes' : 'Add notes'" @click="openNotesModal(entry)">
            <AppIcon name="edit" :size="16" color="var(--muted)" />
          </button>
        </div>
      </div>
    </div>

    <Modal
      v-if="notesModal"
      :title="notesModal.name || notesModal.domain || 'Notes'"
      @close="closeNotesModal"
    >
      <div class="field">
        <label>Notes</label>
        <textarea
          v-model="draftNotes"
          class="input notesInput"
          placeholder="Add notes about this customer…"
          rows="5"
        />
      </div>
      <template #footer>
        <button class="btn" @click="closeNotesModal">Cancel</button>
        <button class="btn primary" :disabled="notesSaving" @click="saveNotes">
          {{ notesSaving ? 'Saving…' : 'Save' }}
        </button>
      </template>
    </Modal>
  </PageChrome>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, computed } from 'vue'
import PageChrome from '../components/PageChrome.vue'
import AppIcon from '../../components/AppIcon.vue'
import HeaderWithIcon from '../../components/HeaderWithIcon.vue'
import Modal from '../../components/Modal.vue'
import { apiFetch } from '../../services/api'
import { useChatScope } from '../composables/useChatScope'

const { setScope, clearScope } = useChatScope()

interface CustomerListEntry {
  id: string
  salesforce_account_id: string
  name: string | null
  domain: string | null
  first_closed_won_date: string | null
  last_closed_won_date: string | null
  opportunity_count: number
  total_amount: number | null
  notes: string | null
  notes_updated_at: string | null
  synced_at: string | null
  enriched_at: string | null
  created_at: string
}

const entries = ref<CustomerListEntry[]>([])
const loading = ref(true)
const error = ref<string | null>(null)

const notesModal = ref<CustomerListEntry | null>(null)
const draftNotes = ref('')
const notesSaving = ref(false)
const notesError = ref('')

const lastSyncedDisplay = computed(() => {
  const first = entries.value[0]
  if (!first?.synced_at) return null
  try {
    return new Date(first.synced_at).toLocaleDateString()
  } catch {
    return null
  }
})

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString()
  } catch {
    return iso
  }
}

function fmtAmount(amount: number | null): string {
  if (amount == null) return ''
  if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1)}M`
  if (amount >= 1_000) return `$${(amount / 1_000).toFixed(0)}K`
  return `$${amount.toFixed(0)}`
}

function openNotesModal(entry: CustomerListEntry) {
  notesModal.value = entry
  draftNotes.value = entry.notes || ''
  notesError.value = ''
}

function closeNotesModal() {
  notesModal.value = null
  draftNotes.value = ''
  notesError.value = ''
}

async function saveNotes() {
  const entry = notesModal.value
  if (!entry) return
  notesSaving.value = true
  notesError.value = ''
  try {
    const updated: CustomerListEntry = await apiFetch(`/customer-list/${entry.id}/notes`, {
      method: 'PATCH',
      body: {
        notes: draftNotes.value.trim() || null,
        expected_notes_updated_at: entry.notes_updated_at,
      },
    })
    const idx = entries.value.findIndex(e => e.id === entry.id)
    if (idx >= 0) entries.value[idx] = { ...updated }
    closeNotesModal()
  } catch (e: any) {
    notesError.value = e?.status === 409
      ? 'Notes were updated by someone else. Please close and try again.'
      : (e?.message || 'Save failed')
  } finally {
    notesSaving.value = false
  }
}

async function load() {
  loading.value = true
  error.value = null
  try {
    entries.value = await apiFetch('/customer-list')
  } catch (e: any) {
    error.value = e?.message || 'Failed to load customer list'
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  setScope({ scopeType: 'workspace', scopeSubjectId: '', pageContext: null })
  load()
})

onBeforeUnmount(() => {
  clearScope()
})
</script>

<style scoped>
.row {
  display: grid;
  grid-template-columns: 260px 120px 180px 1fr 36px;
  gap: 12px;
  align-items: center;
  padding: 12px 10px;
  border-bottom: 1px solid var(--border2);
}

.row:hover {
  background: var(--row-hover-bg);
}

.statCol {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.statLabel {
  color: var(--muted);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.statValue {
  color: var(--text);
  font-size: 13px;
  white-space: nowrap;
}

.notesPreview {
  min-width: 0;
}

.notesText {
  font-size: 13px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
}

.noNotes {
  font-size: 13px;
}

.editBtn {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  justify-self: end;
}

.editBtn:hover {
  background: var(--row-hover-bg);
}

.notesInput {
  width: 100%;
  resize: vertical;
  font-family: inherit;
  font-size: 14px;
  min-height: 100px;
}

.error {
  color: var(--danger, #dc2626);
  font-size: 13px;
}
</style>
